import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import styled from "styled-components";
import { fetchCarpetById } from "../aws/DynamoDB";

export default function CarpetDetail() {
    const location = useLocation();
    const { state } = location;
    const { id } = useParams();
    const navigate = useNavigate();

    const [carpet, setCarpet] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null);
    const [zoom, setZoom] = useState(1);
    const [imageIndex, setImageIndex] = useState(0);
    const [dialogOpen, setDialogOpen] = useState(false);

    useEffect(() => {
        const fetchCarpet = async () => {
            if (!id) {
                navigate("/carpets");
                return;
            }

            let foundCarpet = state?.carpet || await fetchCarpetById(id);

            if (foundCarpet) {
                setCarpet(foundCarpet);
                setSelectedImage(foundCarpet.images[0] || "");
                setImageIndex(0);
            } else {
                navigate("/carpets");
            }

            setLoading(false);
        };

        fetchCarpet();
    }, [id, navigate, state]);

    const openModal = () => {
        setDialogOpen(true);
        setZoom(1);
    };

    const handleNextImage = () => {
        const nextIndex = (imageIndex + 1) % carpet.images.length;
        setImageIndex(nextIndex);
        setSelectedImage(carpet.images[nextIndex]);
        setZoom(1);
    };

    const handlePrevImage = () => {
        const prevIndex = (imageIndex - 1 + carpet.images.length) % carpet.images.length;
        setImageIndex(prevIndex);
        setSelectedImage(carpet.images[prevIndex]);
        setZoom(1);
    };

    if (loading) return <LoadingMessage>Lädt Teppichdetails...</LoadingMessage>;
    if (!carpet) return null;

    return (
        <Wrapper>
            {/* Image Section */}
            <ImageContainer>
                <PreviewFrame onClick={openModal}>
                    <img src={selectedImage} alt="Selected Carpet" />
                </PreviewFrame>
                <ThumbnailContainer>
                    {carpet.images.map((image, index) => (
                        <Thumbnail
                            key={index}
                            src={image}
                            alt={`Thumbnail ${index + 1}`}
                            onClick={() => {
                                setSelectedImage(image);
                                setImageIndex(index);
                            }}
                            active={selectedImage === image}
                        />
                    ))}
                </ThumbnailContainer>
            </ImageContainer>

            {/* Carpet Details Section */}
            <DetailContainer>
                <h1>{carpet.name}</h1>
                {/*<ShortDescription>{carpet.shortDescription}</ShortDescription>*/}

                <CarpetMetric>
                    <DetailRow>Modelle: <strong>{carpet.model || "N/A"}</strong></DetailRow>
                    <DetailRow>Maßen: <strong>{carpet.size} cm</strong></DetailRow>
                    <DetailRow>
                        Preis:
                        {carpet.priceOnRequest ? (
                            <strong style={{ marginLeft: "5px" }}> Auf Anfrage</strong>
                        ) : carpet.acctPrice && carpet.orgPrice && parseFloat(carpet.acctPrice) < parseFloat(carpet.orgPrice) ? (
                            <strong style={{ marginLeft: "5px" }}>
                                  <span style={{ textDecoration: 'line-through', color: 'gray', marginRight: '8px' }}>
                                      € {parseFloat(carpet.orgPrice).toFixed(2)}
                                  </span>
                                <span style={{ color: 'red', fontWeight: 'bold' }}>
                                      € {parseFloat(carpet.acctPrice).toFixed(2)}
                                  </span>
                            </strong>
                        ) : (
                            <strong>
                                € {(carpet.acctPrice ? parseFloat(carpet.acctPrice) : parseFloat(carpet.orgPrice || 0)).toFixed(2)}
                            </strong>
                        )}
                    </DetailRow>
                    <DetailRow>Objekt Nummer: <strong>{carpet.id}</strong></DetailRow>

                    {/*<DetailRow>Material: <strong>{carpet.material || "N/A"}</strong></DetailRow>*/}
                    {/*<DetailRow>Herstellung: <strong>{carpet.manufacturing || "N/A"}</strong></DetailRow>*/}
                    {/*<DetailRow>Design: <strong>{carpet.design || "N/A"}</strong></DetailRow>*/}
                    {/*<DetailRow>Nutzung: <strong>{carpet.usage || "N/A"}</strong></DetailRow>*/}
                    {/*<DetailRow>Farbe: <strong>{carpet.color || "N/A"}</strong></DetailRow>*/}
                    {/*<DetailRow>Beitrag Erstellt am: <strong>{new Date(carpet.created_time).toLocaleDateString()}</strong></DetailRow>*/}
                </CarpetMetric>

                <DetailedDescription>{carpet.detailedDescription}</DetailedDescription>
            </DetailContainer>

            {/* Image Modal */}
            {dialogOpen && selectedImage && (
                <Modal onClick={() => setDialogOpen(false)}>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                        <CloseButton onClick={() => setDialogOpen(false)}>×</CloseButton>
                        <ArrowButton left onClick={handlePrevImage}>❮</ArrowButton>
                        <ZoomControls>
                            <button onClick={() => setZoom((prev) => Math.max(prev - 0.2, 1))}>-</button>
                            <button onClick={() => setZoom((prev) => Math.min(prev + 0.5, 3))}>+</button>
                        </ZoomControls>
                        <ZoomImageWrapper>
                            <ZoomImage style={{ transform: `scale(${zoom})` }} src={selectedImage} alt="Zoomed Carpet" />
                        </ZoomImageWrapper>
                        <ArrowButton right onClick={handleNextImage}>❯</ArrowButton>
                    </ModalContent>
                </Modal>
            )}
        </Wrapper>
    );
}
/* Styled Components */
const Wrapper = styled.div`
  max-width: 1400px;
  margin: 60px auto 0;
  
  padding-top: 25px;
  display: flex;
  flex-direction: column;
  //gap: 20px;
`;

const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const PreviewFrame = styled.div`
  //width: 400px;
  height: 550px;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
  border-radius: 8px;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  @media (max-width: 768px) {
    width: 90%; /* Ensures it stays proportional */
    max-width: 500px; /* Prevents shrinking too much */
    height: 350px; /* Keeps a consistent height */
  }
`;


const ThumbnailContainer = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 10px;
  max-width: 100%;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const Thumbnail = styled.img`
  //width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 5px;
  cursor: pointer;
  border: ${({active}) => (active ? "2px solid #7620FF" : "2px solid transparent")};
  transition: 0.3s;
  border-color: #e7e7e7;

  &:hover {
    border-color: #6b6b6b;
  }
`;

const DetailContainer = styled.div`
  padding: 10px;
  background: #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
`;

const CarpetMetric = styled.div`
  display: flex;
  gap:10px;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;

`;



const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;


const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 24px;
  background: none;
  border: none;
  cursor: pointer;
  z-index: 1000; /* Keeps button on top */
`;



const ArrowButton = styled.button`
  position: absolute;
  top: 50%;
  ${({left}) => left && 'left: 10px;'}
  ${({right}) => right && 'right: 10px;'}
  font-size: 30px;
  background: none;
  border: none;
  color: #818181;
  cursor: pointer;
  transform: translateY(-50%);
  z-index: 1000; /* Keeps arrows above zoomed image */
  
`;

const ModalContent = styled.div`
  position: relative;
  background: #fff;
  width: 90%;
  height: 90vh;
  max-width: 900px;
  max-height: 90vh;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 95%;
    height: 85vh;
  }
`;

const ZoomImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const ZoomImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain; /* Ensures entire image fits inside modal */
  transition: transform 0.3s ease;
  pointer-events: auto;
`;


const LoadingMessage = styled.p`
  text-align: center;
  font-size: 18px;
  color: #707070;
  padding: 50px 0;
`;

const DetailRow = styled.p`
  margin: 10px 0;
  font-size: 20px;
`;

const ShortDescription = styled.p`
  font-size: 20px;
  font-style: italic;
  color: #0e0e0e;
`;

const DetailedDescription = styled.p`
  font-size: 18px;
  color: #0a0a0a;
  line-height: 1.6;
`;

const ZoomControls = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  display: flex;
  gap: 10px;
  z-index: 1000; /* Keeps zoom buttons above image */

  button {
    background: #fff;
    border: 1px solid #ccc;
    padding: 5px 10px;
    font-size: 18px;
    cursor: pointer;
    transition: 0.3s;
    border-radius: 5px;

    &:hover {
      background: #ddd;
    }
  }
`;

